import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { type Theme, useTheme } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";

import { ReactComponent as DarkLogo } from "../../assets/packnet-platform-dark.svg";
import { ReactComponent as Logo } from "../../assets/packnet-platform-light.svg";

export const getLinearGradient = (theme: Theme) =>
	theme.palette.mode === "dark"
		? `linear-gradient(to right, ${theme.palette.background.paper} 30%, #B9BE10 50%, ${theme.palette.background.paper} 70%)`
		: "linear-gradient(to right, #E1E1C5 30%, #B9BE10 50%, #E1E1C5 70%)";

const LOGO_WIDTH = 300;
const SHOW_RELOAD_TIMEOUT = 10 * 1000;

export interface LoadingScreenProps {
	children?: never;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = () => {
	const [showReload, setShowReload] = useState(false);
	const theme = useTheme();

	useEffect(() => {
		const timeout = setTimeout(() => {
			setShowReload(true);
		}, SHOW_RELOAD_TIMEOUT);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Grid
			container
			direction="column"
			gap={2}
			justifyContent="center"
			sx={{ height: "100%", width: "fit-content", margin: "auto" }}
			data-testid="loading-screen"
			display="grid"
			gridTemplateRows="1fr min-content 1fr"
		>
			<Stack gap={2} sx={{ gridRow: 2 }}>
				{theme.palette.mode === "dark" ? <DarkLogo width={LOGO_WIDTH} /> : <Logo width={LOGO_WIDTH} />}

				<Box
					sx={{
						"@keyframes loading": {
							"0%": {
								backgroundPosition: "100% 0",
							},
							"50%": {
								backgroundPosition: "0 0",
							},
							"100%": {
								backgroundPosition: "100% 0",
							},
						},

						height: 8,
						width: "100%",
						borderRadius: 1,
						background: getLinearGradient,
						backgroundSize: "200%",
						backgroundPosition: "100% 0",
						animation: "loading 3s cubic-bezier(0.45, 0, 0.55, 1) infinite",
					}}
					data-testid="loading-screen__bar"
				/>
			</Stack>

			{Boolean(showReload) && (
				<Grid container direction="column" gap={1} sx={{ gridRow: 3, textAlign: "center" }}>
					<Typography variant="body2">
						<FormattedMessage id="loading-screen.reload-message" defaultMessage="This is taking longer than usual..." />
					</Typography>

					<Button variant="text" size="small" onClick={() => window.location.reload()}>
						<FormattedMessage id="loading-screen.reload-button" defaultMessage="Reload" />
					</Button>
				</Grid>
			)}
		</Grid>
	);
};
